// packages/ui/src/Modal.tsx

import React, { useEffect, useRef, useId } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { IconButton } from './IconButton';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  titleKey?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
  closeOnOverlayClick?: boolean;
  closeOnEscapeKey?: boolean;
  hideCloseButton?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
  dialogClassName?: string;
  overlayClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  id?: string;
  /**
   * Receives the mounted panel element so nested overlays can portal into the modal surface.
   */
  onPanelMount?: (element: HTMLDivElement | null) => void;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  titleKey,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscapeKey = true,
  hideCloseButton = false,
  initialFocusRef,
  dialogClassName,
  overlayClassName,
  headerClassName,
  bodyClassName,
  footerClassName,
  id,
  onPanelMount,
}) => {
  const { t } = useTranslation();
  const autoId = useId();
  const modalId = id || autoId;
  const titleId = `${modalId}-title`;

  const resolvedTitle = titleKey ? t(titleKey) : title;
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onPanelMount) {
      onPanelMount(isOpen ? modalRef.current : null);
    }
  }, [isOpen, onPanelMount]);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === 'Escape' && closeOnEscapeKey) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, closeOnEscapeKey]);

  // Handle initial focus & focus trapping (existing logic)
  useEffect(() => {
    if (isOpen) {
      const focusTimeout = window.setTimeout(() => {
        if (initialFocusRef?.current) {
          initialFocusRef.current.focus();
        } else if (modalRef.current) {
          const closeButton = modalRef.current.querySelector('button[aria-label="Close modal"]') as HTMLElement;
          if (closeButton && !hideCloseButton) {
            closeButton.focus();
          } else {
            const primaryActionButton = modalRef.current.querySelector<HTMLButtonElement>('button[data-modal-primary-action="true"]');
            if (primaryActionButton && !primaryActionButton.disabled) {
              primaryActionButton.focus();
            } else {
              modalRef.current.focus();
            }
          }
        }
      }, 100);
      const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        const trapFocus = (e: KeyboardEvent) => {
          if (e.key === 'Tab') {
            if (e.shiftKey) {
              if (document.activeElement === firstElement) {
                lastElement.focus();
                e.preventDefault();
              }
            } else {
              if (document.activeElement === lastElement) {
                firstElement.focus();
                e.preventDefault();
              }
            }
          }
        };
        const currentModalRef = modalRef.current;
        currentModalRef?.addEventListener('keydown', trapFocus);
        return () => {
          window.clearTimeout(focusTimeout);
          currentModalRef?.removeEventListener('keydown', trapFocus);
        };
      }

      return () => {
        window.clearTimeout(focusTimeout);
      };
    }
  }, [isOpen, initialFocusRef, hideCloseButton]);

  const sizeClasses = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    full: 'max-w-full h-full rounded-none',
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          aria-labelledby={resolvedTitle ? titleId : undefined}
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className={clsx(
              "fixed inset-0",
              // Neobrutalism
              "bg-black/50",
              // Liquid glass
              "[.theme-liquid-glass_&]:bg-slate-900/60 [.theme-liquid-glass_&]:backdrop-blur-sm",
              overlayClassName
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={closeOnOverlayClick ? onClose : undefined}
          />

          <motion.div
            ref={modalRef}
            tabIndex={-1}
            className={clsx(
              "relative flex flex-col m-auto",
              "focus:outline-none",
              // Neobrutalism
              "bg-white rounded-xl border-2 border-black shadow-2xl",
              // Liquid glass
              "[.theme-liquid-glass_&]:bg-slate-800/80 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20",
              "[.theme-liquid-glass_&]:rounded-3xl [.theme-liquid-glass_&]:backdrop-blur-2xl",
              "[.theme-liquid-glass_&]:shadow-[0_24px_64px_rgba(0,0,0,0.4)]",
              sizeClasses[size],
              size === 'full' ? 'w-full h-full' : 'max-h-[90vh]',
              dialogClassName
            )}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25, duration: 0.2 }}
          >
            {(resolvedTitle || !hideCloseButton) && (
              <div
                className={clsx(
                  "flex items-center justify-between p-4 sm:p-5",
                  // Neobrutalism
                  "border-b-2 border-black",
                  // Liquid glass
                  "[.theme-liquid-glass_&]:border-b [.theme-liquid-glass_&]:border-white/20",
                  headerClassName
                )}
              >
                {resolvedTitle ? (
                  <h2 id={titleId} className={clsx(
                    "text-lg font-bold",
                    // Neobrutalism
                    "text-gray-900",
                    // Liquid glass
                    "[.theme-liquid-glass_&]:text-white"
                  )}>
                    {resolvedTitle}
                  </h2>
                ) : <div />}
                {!hideCloseButton && (
                  <IconButton
                    icon={<X size={20} />}
                    onClick={onClose}
                    ariaLabel="Close modal"
                    variant="ghost"
                    size="sm"
                    className="ml-auto -mr-2"
                    applyNeobrutalistEffects={false}
                  />
                )}
              </div>
            )}

            <div className={clsx("p-4 sm:p-6 overflow-y-auto flex-grow", bodyClassName)}>
              {children}
            </div>

            {footer && (
              <div
                className={clsx(
                  "flex items-center justify-end p-4 sm:p-5 space-x-3",
                  // Neobrutalism
                  "border-t-2 border-black",
                  // Liquid glass
                  "[.theme-liquid-glass_&]:border-t [.theme-liquid-glass_&]:border-white/20",
                  footerClassName
                )}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
